import { mergeClassNames } from '@/utils/mergeClassNames';
import { ButtonContext, IconButtonContext } from '@zapier/design-system';
import { useMemo } from 'react';
import {
  Provider,
  Menu as ReactAriaMenu,
  MenuItem as ReactAriaMenuItem,
  MenuItemProps as ReactAriaMenuItemProps,
  MenuProps as ReactAriaMenuProps,
  MenuTrigger as ReactAriaMenuTrigger,
  MenuTriggerProps as ReactAriaMenuTriggerProps,
  Popover as ReactAriaPopover,
  PopoverProps as ReactAriaPopoverProps,
  Separator as ReactAriaSeparator,
} from 'react-aria-components';
import { MenuContext, useMenuContext, type MenuSize } from './MenuContext';
import styles from './MenuTrigger.module.scss';

export type MenuTriggerRootProps = ReactAriaMenuTriggerProps & {
  /**
   * The size of the menu and its subcomponents.
   * @default 'medium'
   */
  size?: MenuSize;
};

/**
 * `MenuTrigger` is used to display a list of actions or options that a user can choose.
 *
 * Built with React Aria's
 * [Popover](https://react-spectrum.adobe.com/react-aria/Popover.html) and
 * [Menu](https://react-spectrum.adobe.com/react-aria/Menu.html) components.
 */
export const MenuTriggerRoot = ({
  size = 'medium',
  ...props
}: MenuTriggerRootProps) => {
  const contextValue = useMemo(() => ({ size }), [size]);

  const buttonProps = { size };
  const iconButtonProps = { size: size === 'compact' ? 'small' : size };

  return (
    <Provider
      values={[
        [ButtonContext, buttonProps],
        [IconButtonContext, iconButtonProps],
      ]}
    >
      <MenuContext.Provider value={contextValue}>
        <ReactAriaMenuTrigger {...props} />
      </MenuContext.Provider>
    </Provider>
  );
};

export type MenuTriggerPopoverProps = ReactAriaPopoverProps;

export const MenuTriggerPopover = ({
  className,
  ...props
}: MenuTriggerPopoverProps) => {
  return (
    <ReactAriaPopover
      {...props}
      className={(renderProps) =>
        mergeClassNames([styles.popover, className], renderProps)
      }
    />
  );
};

export type MenuTriggerListProps<T> = ReactAriaMenuProps<T>;

export const MenuTriggerList = <T extends object>(
  props: MenuTriggerListProps<T>
) => {
  return <ReactAriaMenu className={styles.list} {...props} />;
};

export type MenuTriggerItemProps = ReactAriaMenuItemProps & {
  /** The visual style of the `MenuTriggerItem`. */
  variant?: 'primary' | 'danger';
};

export const MenuTriggerItem = ({
  variant = 'primary',
  className,
  ...props
}: MenuTriggerItemProps) => {
  const { size } = useMenuContext();

  return (
    <ReactAriaMenuItem
      {...props}
      data-size={size}
      data-variant={variant}
      className={mergeClassNames([styles.item, className])}
    />
  );
};

export const MenuTriggerSeparator = () => {
  return <ReactAriaSeparator className={styles.separator} />;
};

export const MenuTrigger = {
  /**
   * The wrapper that goes around the whole menu.
   */
  Root: MenuTriggerRoot,
  /**
   * The popover that contains the menu.
   */
  Popover: MenuTriggerPopover,
  /**
   * The actual menu containing menu items.
   */
  List: MenuTriggerList,
  /**
   * The menu items that can execute actions.
   */
  Item: MenuTriggerItem,
  /**
   * A visual separator between action items.
   */
  Separator: MenuTriggerSeparator,
};

export default MenuTrigger;
